/**
 * contributionTilesConfig.tsx
 *
 * Provides configuration for contribution tiles in the Contribution Portal.
 * Exports types and utility functions for tile styling and tile data.
 */

import {
  HelpIcon,
  ReadGuideIcon,
  CreateRecipeIcon,
} from "../../assets/icons/ContributionPortalIcons";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { Theme } from "@mui/material/styles";
import React from "react";

/**
 * ContributionTile type.
 * Represents either a link tile (with href) or an action tile (with onClick).
 */
export type ContributionTile =
  | {
      icon: React.ReactNode;
      label: string;
      href: string;
      target?: string;
      sx?: object;
      onClick?: never;
    }
  | {
      icon: React.ReactNode;
      label: string;
      onClick: () => void;
      sx?: object;
      href?: never;
      target?: never;
    };

/**
 * Returns style object for the "Need Help?" tile.
 * Adds a background image and hover border.
 */
export const getHelpTileStyle = (theme: Theme) => ({
  background: `url(${getStaticAssetPath(
    "images/helpBg.png"
  )}) center / cover no-repeat`,
  color: theme.palette.materiom.charcoal,
  position: "relative",
  zIndex: 1,
  "&:hover": {
    border: `1px solid ${theme.palette.materiom.seaweed}`,
  },
});

/**
 * Returns array of contribution tiles for the portal.
 * - Help: mailto link
 * - Guide: external link
 * - Create: opens modal
 */
export const getContributionTiles = (
  theme: Theme,
  handleOpenModal: (url: string) => void,
  NEW_RECIPE_FORM_URL: string
): ContributionTile[] => [
  {
    icon: <HelpIcon />,
    label: "Need Help?",
    href: "mailto:support@materiom.org",
    sx: getHelpTileStyle(theme),
  },
  {
    icon: <ReadGuideIcon />,
    label: "Read contribution guide",
    href: "https://materiom.org/contribute-guide",
    target: "_blank",
  },
  {
    icon: <CreateRecipeIcon />,
    label: "Create new recipe",
    onClick: () => handleOpenModal(NEW_RECIPE_FORM_URL),
  },
];
